import { getTradingPairs, getDisplaySpread } from '@/api/market'
import { getPositions, getPendingOrders, getTradeHistory, placeOrder as placeOrderApi, closePosition as closePositionApi, cancelPendingOrder as cancelPendingOrderApi, updateSltp as updateSltpApi, getMarginLevel as getMarginLevelApi } from '@/api/trade'
import { getMarketStatus } from '@/api/market'
import { connectQuoteWS, disconnectQuoteWS } from '@/utils/ws-quote'
import { SYMBOLS } from '@/config/constants'
import { getQuickLots } from '@/config/platform'
import Vue from 'vue'

// fallback 默认值：黄金 lotStep=0.01, minLots=0.01; 白银 lotStep=0.1, minLots=0.05
// 实际值以后端 DB 配置为准，此处仅作为后端不可用时的兜底
function getSymbolDefaults(symbol) {
  const isSilver = symbol === 'XAGUSD'
  return {
    minLots: isSilver ? 0.05 : 0.01,
    maxLots: isSilver ? 20 : 10,
    lotStep: isSilver ? 0.1 : 0.01,
    quickLots: getQuickLots(symbol)
  }
}

// 后端 quickLots 可能为 JSON 字符串 "[0.01,0.1,1,10]" 或逗号分隔字符串 "0.01,0.1,1,10"，需解析为数组
function parseQuickLots(val, symbol) {
  if (Array.isArray(val) && val.length) return val
  if (typeof val === 'string' && val.trim()) {
    // 优先尝试 JSON 解析（后端可能存储为 JSON 字符串）
    try {
      const parsed = JSON.parse(val)
      if (Array.isArray(parsed) && parsed.length) return parsed
    } catch { /* 不是 JSON，继续按逗号分隔处理 */ }
    const arr = val.split(',').map(Number).filter(n => !isNaN(n) && n > 0)
    if (arr.length) return arr
  }
  return getSymbolDefaults(symbol).quickLots
}

/* ── 交易对配置缓存 (symbol → { marginPerLot, tradeFeeRate, defaultLeverage, quickLots, ... }) ── */

// ============ 账户资金指标实时联动重算 ============
// 每次持仓浮动盈亏变化时，同帧重算：账户净值、可用保证金、预付款比例、风控状态，
// 保证四个数字与浮动盈亏同源同帧跳动，用户肉眼可验算：
//   净值 = 余额 + 浮动盈亏
//   可用保证金 = 净值 - 占用保证金
//   预付款比例 = 净值 / 占用保证金 × 100%
// ⚠️ 此公式必须与 app 端（ftx-app/src/store/modules/ftx/trade.js）及后端 MT5 标准
// （ftx_account_service.py get_margin_level、ftx_liquidation_service.py）保持一致。
// 余额（usdBalance）与占用保证金（frozenMargin）为慢变量，由 30 秒轮询 /margin/level
// 接口提供基准值并在开平仓后主动刷新，此处不重复维护资金账本。
function recalcMarginIndicators(state) {
  const ml = state.marginLevel
  const usdBalance = Number(ml.usdBalance) || 0
  const frozenMargin = Number(ml.frozenMargin) || 0
  // 复用持仓已算好的实时 pnl（做多用 ask、做空用 bid，与页面浮动盈亏同源）
  const totalPnl = state.positions.reduce((sum, p) => sum + (Number(p.pnl) || 0), 0)

  // 净值 = 余额 + 浮动盈亏
  const equity = Math.round((usdBalance + totalPnl) * 100) / 100
  // 可用保证金 = 净值 - 占用保证金（负数原样保留，红色警示展示，不截断）
  const availableBalance = Math.round((equity - frozenMargin) * 100) / 100

  let marginLevel = 0
  let status = 'safe'
  if (frozenMargin > 0) {
    // 预付款比例 = 净值 / 占用保证金 × 100%（MT5 标准）
    marginLevel = Math.round((equity / frozenMargin) * 10000) / 100
    // 风控状态与后端判定规则一致：< 强平线 danger，≤ 预警线 warning，否则 safe
    const forceCloseRatio = Number(ml.forceCloseRatio) || 50
    const marginCallRatio = Number(ml.marginCallRatio) || 100
    if (marginLevel < forceCloseRatio) {
      status = 'danger'
    } else if (marginLevel <= marginCallRatio) {
      status = 'warning'
    } else {
      status = 'safe'
    }
  }

  state.marginLevel = { ...ml, equity, availableBalance, marginLevel, status }
}

const state = {
  currentSymbol: 'XAUUSD',
  marketData: {},
  tradingPairConfig: {},
  positions: [],
  pendingOrders: [],
  tradeHistory: [],
  orderType: 'market', // market | limit
  activePeriod: '1m',
  activeIndicators: { ma: false, macd: true, boll: false },
  priceInterval: null,
  marginLevel: {
    marginLevel: 0,
    equity: 0,
    usdBalance: 0,
    frozenMargin: 0,
    availableBalance: 0,
    marginCallRatio: 100,
    forceCloseRatio: 50,
    status: 'safe'
  },
  marketStatus: {
    market_open: true,
    reason: '',
    next_open: null,
    next_close: null
  }
}

const getters = {
  currentSymbol: s => s.currentSymbol,
  currentMarket: s => s.marketData[s.currentSymbol],
  currentPairConfig: s => s.tradingPairConfig[s.currentSymbol] || {},
  marketData: s => s.marketData,
  tradingPairConfig: s => s.tradingPairConfig,
  positions: s => s.positions,
  pendingOrders: s => s.pendingOrders,
  tradeHistory: s => s.tradeHistory,
  orderType: s => s.orderType,
  activePeriod: s => s.activePeriod,
  activeIndicators: s => s.activeIndicators,
  totalFloatingPnl: s => {
    if (!s.positions.length) return 0
    return Math.round(s.positions.reduce((sum, pos) => sum + (pos.pnl || 0), 0) * 100) / 100
  },
  marginLevelData: s => s.marginLevel,
  marginStatus: s => s.marginLevel.status || 'safe',
  isMarginDanger: s => s.marginLevel.status === 'danger',
  isMarginWarning: s => s.marginLevel.status === 'warning'
}

const mutations = {
  SET_SYMBOL(state, symbol) {
    // 规范化交易对代码：别名 → 真实代码
    const aliasMap = { gold: 'XAUUSD', silver: 'XAGUSD', dxy: 'USDX' }
    state.currentSymbol = aliasMap[symbol] || symbol
  },
  SET_MARKET_DATA(state, data) {
    state.marketData = { ...state.marketData, ...data }
  },
  SET_TRADING_PAIR_CONFIG(state, config) {
    state.tradingPairConfig = { ...state.tradingPairConfig, ...config }
  },
  UPDATE_PRICE(state, { symbol, price, bid, ask, chg, chgPct, vol24h }) {
    if (state.marketData[symbol]) {
      const prev = state.marketData[symbol]
      state.marketData[symbol] = {
        ...prev,
        price: price || prev.price + (Math.random() - 0.5) * prev.spread / 100,
        bid: bid || (price || prev.price) - prev.spread / 200,
        ask: ask || (price || prev.price) + prev.spread / 200,
        chg: chg ?? prev.chg,
        chgPct: chgPct ?? prev.chgPct,
        vol24h: vol24h ?? prev.vol24h
      }
    } else {
      // symbol 不存在时创建新条目（WebSocket 推送先于 fetchMarketData 完成的情况）
      state.marketData[symbol] = {
        name: symbol,
        code: symbol,
        price: price || bid || ask || 0,
        bid: bid || 0,
        ask: ask || 0,
        chg: chg || 0,
        chgPct: chgPct || 0,
        spread: 0,
        vol24h: 0,
        high: 0,
        low: 0
      }
      // 触发响应式更新
      state.marketData = { ...state.marketData }
    }
    // 实时更新持仓盈亏（做多用bid/平仓价，做空用ask/平仓价，与后端一致）
    if (state.positions.length && (bid || ask || price)) {
      state.positions = state.positions.map(pos => {
        if (pos.symbol !== symbol) return pos
        const openPrice = pos.openPrice
        const lots = pos.lots
        // 做多用 bid（卖出市价/平仓价），做空用 ask（买入市价/平仓价），与后端 ftx_trade_service.py 一致
        const currentPrice = pos.direction === 'buy' ? (bid || ask || price) : (ask || bid || price)
        // 合约乘数按品种从后台配置读取（无感同步），取不到回退 100；点差已含在成交价中无需额外扣减
        const contractSize = state.tradingPairConfig[symbol]?.contractSize || 100
        let pnl = 0
        if (pos.direction === 'buy') {
          pnl = (currentPrice - openPrice) * lots * contractSize
        } else if (pos.direction === 'sell') {
          pnl = (openPrice - currentPrice) * lots * contractSize
        }
        return { ...pos, currentPrice, pnl: Math.round(pnl * 100) / 100 }
      })
    }
    // 实时更新历史中持仓单的浮动盈亏（做多用bid/平仓价，做空用ask/平仓价）
    if (state.tradeHistory.length && (bid || ask || price)) {
      state.tradeHistory = state.tradeHistory.map(h => {
        if (!h.isOpen || h.symbol !== symbol) return h
        const openPrice = h.openPrice
        const lots = h.lots
        const currentPrice = h.direction === 'buy' ? (bid || ask || price) : (ask || bid || price)
        const contractSize = state.tradingPairConfig[symbol]?.contractSize || 100
        let pnl = 0
        if (h.direction === 'buy') {
          pnl = (currentPrice - openPrice) * lots * contractSize
        } else if (h.direction === 'sell') {
          pnl = (openPrice - currentPrice) * lots * contractSize
        }
        pnl = Math.round(pnl * 100) / 100
        return { ...h, currentPrice, pnl, floatingPnl: pnl }
      })
    }
    // 持仓盈亏变化后同帧重算净值/可用保证金/预付款比例/风控状态，与浮动盈亏同帧联动
    recalcMarginIndicators(state)
  },
  SET_POSITIONS(state, list) {
    // 立即用本地最新报价重算 PnL，避免下单/平仓后要等下一个 WS tick 才更新（白银 tick 稀疏时感知明显）
    state.positions = (list || []).map(pos => {
      const md = state.marketData[pos.symbol]
      if (!md || !(md.bid || md.ask || md.price)) return pos
      // 做多用 bid/平仓价，做空用 ask/平仓价，与 UPDATE_PRICE / 后端一致
      const currentPrice = pos.direction === 'buy' ? (md.bid || md.ask || md.price) : (md.ask || md.bid || md.price)
      const openPrice = pos.openPrice
      const lots = pos.lots
      const contractSize = state.tradingPairConfig[pos.symbol]?.contractSize || 100
      let pnl = 0
      if (pos.direction === 'buy') {
        pnl = (currentPrice - openPrice) * lots * contractSize
      } else if (pos.direction === 'sell') {
        pnl = (openPrice - currentPrice) * lots * contractSize
      }
      return { ...pos, currentPrice, pnl: Math.round(pnl * 100) / 100 }
    })
    // 持仓变化（开仓/平仓）后同步重算净值/可用保证金/预付款比例
    recalcMarginIndicators(state)
  },
  SET_PENDING_ORDERS(state, list) {
    state.pendingOrders = list
  },
  SET_TRADE_HISTORY(state, list) {
    state.tradeHistory = list
  },
  SET_ORDER_TYPE(state, type) {
    state.orderType = type
  },
  SET_PERIOD(state, period) {
    state.activePeriod = period
  },
  TOGGLE_INDICATOR(state, key) {
    state.activeIndicators = { ...state.activeIndicators, [key]: !state.activeIndicators[key] }
  },
  SET_PRICE_INTERVAL(state, id) {
    state.priceInterval = id
  },
  SET_MARGIN_LEVEL(state, data) {
    state.marginLevel = { ...state.marginLevel, ...data }
    // 轮询校准基准值（余额/占用保证金）更新后，立即用最新持仓 pnl 重算资金指标，
    // 避免接口返回的静态快照与实时盈亏脱节
    recalcMarginIndicators(state)
  },
  SET_MARKET_STATUS(state, data) {
    state.marketStatus = { ...state.marketStatus, ...data }
  }
}

const actions = {
  switchSymbol({ commit }, symbol) {
    commit('SET_SYMBOL', symbol)
  },

  async fetchMarketData({ commit }) {
    try {
      const res = await getTradingPairs()
      const pairs = res.data || []
      const marketMap = {}
      const configMap = {}
      pairs.forEach(p => {
        marketMap[p.symbol] = {
          name: p.name || p.symbol,
          code: p.symbol,
          price: p.bid || p.ask || 0,
          bid: p.bid || 0,
          ask: p.ask || 0,
          chg: p.change || 0,
          chgPct: p.changeRate || 0,
          spread: p.spread || 0,
          vol24h: p.volume || p.vol24h || 0,
          high: p.high || 0,
          low: p.low || 0
        }
        const defaults = getSymbolDefaults(p.symbol)
        configMap[p.symbol] = {
          contractSize: p.contractSize || 100,
          marginPerLot: p.marginPerLot || 1000,
          tradeFeeRate: p.tradeFeeRate ?? 0.0010,
          defaultLeverage: p.defaultLeverage || 100,
          leverageOptions: p.leverageOptions || [100],
          minLots: p.minLots || defaults.minLots,
          maxLots: p.maxLots || defaults.maxLots,
          lotStep: p.lotStep || defaults.lotStep,
          quickLots: parseQuickLots(p.quickLots, p.symbol),
          isTradable: p.isTradable !== false,
          name: p.name || p.symbol,
          displaySpread: p.spread != null ? Number(p.spread) : null
        }
      })
      // /pairs 仅返回可交易品种（USDX 等 is_tradable=0 品种被过滤），
      // 用 SYMBOLS 本地配置补齐仅展示品种，否则 UPDATE_PRICE 会因
      // marketData[symbol] 不存在而丢弃 WS 推送的报价
      if (pairs.length) {
        Object.keys(SYMBOLS).forEach(symbol => {
          if (marketMap[symbol]) return
          const cfg = SYMBOLS[symbol]
          marketMap[symbol] = {
            name: cfg.name || symbol,
            code: symbol,
            price: cfg.defaultPrice || 0,
            bid: cfg.defaultPrice || 0,
            ask: cfg.defaultPrice || 0,
            chg: 0,
            chgPct: 0,
            spread: cfg.spread || 0,
            vol24h: cfg.vol24h || 0,
            high: 0,
            low: 0
          }
        })
      }
      if (Object.keys(marketMap).length) {
        commit('SET_MARKET_DATA', marketMap)
        commit('SET_TRADING_PAIR_CONFIG', configMap)
      }
    } catch {
      // 网络异常时保留已有数据
    }
  },

  // 获取展示点差（从 Redis Cache-Aside 读取）
  async fetchDisplaySpread({ commit, state }, symbol) {
    if (!symbol) return null
    try {
      const res = await getDisplaySpread(symbol)
      if (res.code === 200 && res.data) {
        const spread = Number(res.data.spread)
        const currentConfig = state.tradingPairConfig
        if (currentConfig && currentConfig[symbol]) {
          commit('SET_TRADING_PAIR_CONFIG', {
            ...currentConfig,
            [symbol]: { ...currentConfig[symbol], displaySpread: spread }
          })
        }
        return spread
      }
    } catch (e) {
      console.error('[trade] fetchDisplaySpread failed:', e)
    }
    return null
  },

  async fetchPositions({ commit, state, rootGetters }) {
    // 未登录时不请求（登录/注册页避免触发 401 → 全局登出跳转导致页面刷新）
    if (!rootGetters['user/token']) {
      commit('SET_POSITIONS', [])
      return
    }
    try {
      const res = await getPositions()
      const list = res.data || []
      const mapped = list.map(p => ({
        id: p.id,
        orderNo: p.orderNo,
        symbol: p.symbol,
        symbolName: (state.tradingPairConfig[p.symbol] || {}).name || p.symbol,
        direction: typeof p.direction === 'number' ? (p.direction === 1 ? 'buy' : 'sell') : (p.direction || 'buy'),
        lots: p.lots,
        leverage: p.leverage,
        margin: p.margin,
        openPrice: p.openPrice,
        currentPrice: p.currentPrice,
        slPrice: p.slPrice,
        tpPrice: p.tpPrice,
        pnl: p.profitLoss || 0,
        fee: p.fee || 0,
        swap: p.swap || 0,
        swapPerLot: p.swapPerLot || 0,
        time: p.openTime
      }))
      commit('SET_POSITIONS', mapped)
    } catch {
      commit('SET_POSITIONS', [])
    }
  },

  async fetchPendingOrders({ commit, state, rootGetters }) {
    // 未登录时不请求（登录/注册页避免触发 401 → 全局登出跳转导致页面刷新）
    if (!rootGetters['user/token']) {
      commit('SET_PENDING_ORDERS', [])
      return
    }
    try {
      const res = await getPendingOrders()
      const list = res.data || []
      const mapped = list.map(p => ({
        id: p.id,
        orderNo: p.orderNo,
        symbol: p.symbol,
        symbolName: (state.tradingPairConfig[p.symbol] || {}).name || p.symbol,
        direction: typeof p.direction === 'number' ? (p.direction === 1 ? 'buy' : 'sell') : (p.direction || 'buy'),
        lots: p.lots,
        leverage: p.leverage,
        margin: p.margin,
        price: p.limitPrice,
        slPrice: p.slPrice,
        tpPrice: p.tpPrice,
        fee: p.fee || 0,
        type: p.orderType === 'stop' ? '突破' : '限价',
        status: '待成交',
        time: p.createTime
      }))
      commit('SET_PENDING_ORDERS', mapped)
    } catch {
      commit('SET_PENDING_ORDERS', [])
    }
  },

  async fetchTradeHistory({ commit, state, rootGetters }, params) {
    // 未登录时不请求（登录/注册页避免触发 401 → 全局登出跳转导致页面刷新）
    if (!rootGetters['user/token']) return
    try {
      const res = await getTradeHistory(params)
      const list = res.data?.rows || res.rows || []
      const mapped = list.map(h => {
        const statusMap = { holding: '持仓中', closed: '已平仓', cancelled: '已撤销', pending: '待成交' }
        const typeMap = { market: '市价', limit: '限价', stop: '突破' }
        const isOpen = h.status === 'holding'
        return {
          id: h.id,
          orderNo: h.orderNo,
          symbol: h.symbol,
          symbolName: (state.tradingPairConfig[h.symbol] || {}).name || h.symbol,
          direction: h.direction || 'buy',
          orderType: typeMap[h.orderType] || '市价',
          lots: h.lots,
          leverage: h.leverage,
          margin: h.margin,
          openPrice: h.openPrice,
          closePrice: h.closePrice,
          currentPrice: h.currentPrice || null,
          price: h.closePrice || h.currentPrice || h.openPrice,
          slPrice: h.slPrice,
          tpPrice: h.tpPrice,
          pnl: isOpen ? (h.floatingPnl || 0) : (h.profitLoss || 0),
          floatingPnl: h.floatingPnl || null,
          fee: h.fee || 0,
          swap: h.swap || 0,
          netProfit: h.netProfit != null ? h.netProfit : null,
          status: statusMap[h.status] || '未知',
          statusRaw: h.status,
          isOpen,
          time: h.closeTime || h.openTime,
          openTime: h.openTime,
          closeTime: h.closeTime
        }
      })
      mapped.sort((a, b) => (b.id || 0) - (a.id || 0))
      commit('SET_TRADE_HISTORY', mapped)
    } catch {
      commit('SET_TRADE_HISTORY', [])
    }
  },

  async fetchMarketStatus({ commit }) {
    try {
      const res = await getMarketStatus()
      const data = (res && (res.data || res)) || {}
      commit('SET_MARKET_STATUS', {
        market_open: data.market_open !== false,
        reason: data.reason || '',
        next_open: data.next_open || null,
        next_close: data.next_close || null
      })
    } catch {
      // 网络异常时保留上次状态
    }
  },

  async placeOrder({ dispatch }, orderData) {
    const res = await placeOrderApi(orderData)
    dispatch('fetchPositions')
    dispatch('fetchPendingOrders')
    return res
  },

  async closePosition(context, { orderId, tradePassword, lots, closePrice }) {
    // 不在此刷新持仓：由调用方（ClosePositionModal → closed 事件）统一刷新，避免一键平仓时 N 次重复请求
    const res = await closePositionApi({ orderId, tradePassword, lots, closePrice })
    return res
  },

  async cancelPending({ dispatch }, { orderId, tradePassword }) {
    const res = await cancelPendingOrderApi({ orderId, tradePassword })
    dispatch('fetchPendingOrders')
    return res
  },

  async updateSltp({ dispatch }, { orderId, slPrice, tpPrice, tradePassword }) {
    const res = await updateSltpApi({ orderId, slPrice, tpPrice, tradePassword })
    dispatch('fetchPositions')
    return res
  },

  async startPriceUpdates({ commit, state, dispatch, rootState }) {
    if (state._wsConnected) return

    // 先通过 REST API 加载交易对列表，初始化 marketData
    // 否则 UPDATE_PRICE 会因 marketData[symbol] 不存在而跳过
    await dispatch('fetchMarketData')

    // 从 Redis 获取展示点差（Cache-Aside 模式，首次会从 DB 回写 Redis）
    Object.keys(SYMBOLS).forEach(symbol => {
      dispatch('fetchDisplaySpread', symbol)
    })

    state._wsConnected = true

    connectQuoteWS({
      symbols: Object.keys(SYMBOLS),
      onQuote(symbol, data) {
        if (!symbol || !data) return
        const sessionOpen = data.session_open || data.open_price || 0
        const bid = data.bid || 0
        const chg = sessionOpen ? bid - sessionOpen : 0
        const chgPct = sessionOpen ? (chg / sessionOpen * 100) : (data.change_rate || 0)
        commit('UPDATE_PRICE', {
          symbol,
          price: bid,
          bid: bid,
          ask: data.ask,
          chg: chg,
          chgPct: chgPct,
          vol24h: data.volume || 0
        })
      },
      onSnapshot(snapshot) {
        if (!snapshot) return
        Object.entries(snapshot).forEach(([symbol, data]) => {
          const sessionOpen = data.session_open || data.open_price || 0
          const bid = data.bid || 0
          const chg = sessionOpen ? bid - sessionOpen : 0
          const chgPct = sessionOpen ? (chg / sessionOpen * 100) : (data.change_rate || 0)
          commit('UPDATE_PRICE', {
            symbol,
            price: bid || data.ask,
            bid: bid,
            ask: data.ask,
            chg: chg,
            chgPct: chgPct,
            vol24h: data.volume || 0
          })
        })
      },
      onStatus(status) {
        if (status === 'disconnected') {
          state._wsConnected = false
        }
      },
      onPriceAlert(data) {
        if (!data) return
        // 通过全局事件总线通知组件展示预警提示
        if (Vue.prototype.$alertBus) {
          Vue.prototype.$alertBus.$emit('price-alert', data)
        } else {
          Vue.prototype.$alertBus = new Vue()
          Vue.prototype.$alertBus.$emit('price-alert', data)
        }
      },
      onConfigUpdate(data) {
        if (!data) return
        // 同步配置变更到 Vuex 和 localStorage
        if (data.tradeEnabled !== undefined) {
          commit('user/SET_TRADE_ENABLED', data.tradeEnabled !== 0, { root: true })
        }
        if (data.maintenanceMode !== undefined) {
          commit('user/SET_MAINTENANCE_MODE', !!(data.maintenanceMode === 1 || data.maintenanceMode === true), { root: true })
        }
        // 同步平台配置到 localStorage
        const { syncPlatformConfigFromApi } = require('@/config/platform')
        syncPlatformConfigFromApi(data)
        // 展示点差变更：收到 WS 触发后，从 REST 接口读取 Redis 中的最新值
        if (data.spread !== undefined && data.symbol) {
          dispatch('fetchDisplaySpread', data.symbol.toUpperCase())
        }
        // 同步交易对级别配置（杠杆/保证金/费率/手数）到 tradingPairConfig（无感更新）
        if (data.symbol) {
          const symbol = data.symbol.toUpperCase()
          const current = state.tradingPairConfig[symbol]
          if (current) {
            const update = {}
            if (data.defaultLeverage !== undefined) update.defaultLeverage = data.defaultLeverage
            if (data.leverageOptions !== undefined) {
              // leverageOptions 从后端传来的是逗号分隔字符串 "50,100,200"
              update.leverageOptions = typeof data.leverageOptions === 'string'
                ? data.leverageOptions.split(',').map(Number).filter(n => !isNaN(n) && n > 0)
                : data.leverageOptions
            }
            if (data.contractSize !== undefined) update.contractSize = data.contractSize
            if (data.marginPerLot !== undefined) update.marginPerLot = data.marginPerLot
            if (data.tradeFeeRate !== undefined) update.tradeFeeRate = data.tradeFeeRate
            if (data.minLots !== undefined) update.minLots = data.minLots
            if (data.maxLots !== undefined) update.maxLots = data.maxLots
            if (data.lotStep !== undefined) update.lotStep = data.lotStep
            if (data.quickLots !== undefined) {
              update.quickLots = typeof data.quickLots === 'string'
                ? data.quickLots.split(',').map(Number).filter(n => !isNaN(n) && n > 0)
                : data.quickLots
            }
            // 新增：同步 swap_per_lot 字段（隔夜费配置）
            if (data.swapPerLot !== undefined) update.swapPerLot = data.swapPerLot
            const config = { ...state.tradingPairConfig, [symbol]: { ...current, ...update } }
            commit('SET_TRADING_PAIR_CONFIG', config)
            
            // 新增：立即刷新持仓列表中的 swapPerLot 字段（无感同步到 UI）
            if (data.swapPerLot !== undefined && state.positions.length) {
              const updatedPositions = state.positions.map(pos => {
                if (pos.symbol === symbol) {
                  return { ...pos, swapPerLot: data.swapPerLot }
                }
                return pos
              })
              commit('SET_POSITIONS', updatedPositions)
            }
          }
        }
        // 向后兼容：无 symbol 的全局保证金/费率更新（旧版 fee config 推送）
        if (!data.symbol && (data.marginPerLot !== undefined || data.tradeFeeRate !== undefined)) {
          const config = {}
          Object.keys(state.tradingPairConfig).forEach(symbol => {
            config[symbol] = {
              ...state.tradingPairConfig[symbol],
              marginPerLot: data.marginPerLot !== undefined ? data.marginPerLot : state.tradingPairConfig[symbol].marginPerLot,
              tradeFeeRate: data.tradeFeeRate !== undefined ? data.tradeFeeRate : state.tradingPairConfig[symbol].tradeFeeRate
            }
          })
          if (Object.keys(config).length) {
            commit('SET_TRADING_PAIR_CONFIG', config)
          }
        }
        // 如果包含兑换/手续费相关字段，通过全局事件总线通知 Exchange 页面
        // （仅限全局推送；带 symbol 的是交易对级别配置，不能污染兑换页费率）
        if (!data.symbol && (data.tradeFeeRate !== undefined || data.minExchange !== undefined || data.dailyLimit !== undefined ||
            data.minDeposit !== undefined || data.minWithdraw !== undefined || data.withdrawFee !== undefined ||
            data.depositNetwork !== undefined || data.marginPerLot !== undefined)) {
          if (!Vue.prototype.$exchangeConfigBus) {
            Vue.prototype.$exchangeConfigBus = new Vue()
          }
          Vue.prototype.$exchangeConfigBus.$emit('exchange-config-update', data)
        }
        // 同步更新保证金水平中的强平线 / 预警线（后台修改后无感生效）
        if (data.forceCloseRatio !== undefined || data.marginCallRatio !== undefined) {
          const ml = state.marginLevel
          const newMl = { ...ml }
          if (data.forceCloseRatio !== undefined) newMl.forceCloseRatio = Number(data.forceCloseRatio)
          if (data.marginCallRatio !== undefined) newMl.marginCallRatio = Number(data.marginCallRatio)
          commit('SET_MARGIN_LEVEL', newMl)
          recalcMarginIndicators(state)
        }
      },
      onPositionClosed(data) {
        // 强平通知：只有 uid 匹配当前登录用户时才刷新（避免雷暴——所有客户端同时请求）
        if (!data?.uid) return
        const currentUid = rootState.user?.uid
        if (currentUid && data.uid === currentUid) {
          dispatch('fetchPositions')
          dispatch('fetchMarginLevel')
        }
      }
    })
  },

  stopPriceUpdates({ state }) {
    state._wsConnected = false
    disconnectQuoteWS()
  },

  async fetchMarginLevel({ commit, rootGetters }) {
    // 未登录时不请求（登录/注册页避免触发 401 → 全局登出跳转导致页面刷新）
    if (!rootGetters['user/token']) return
    try {
      const res = await getMarginLevelApi()
      if (res.code === 200 && res.data) {
        commit('SET_MARGIN_LEVEL', res.data)
      }
    } catch {
      // 网络异常时保留已有数据
    }
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
